<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { computed, onMounted, ref } from 'vue';
import { IonButton, IonContent, IonIcon, IonSpinner } from '@ionic/vue';
import { closeOutline, ticketOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import { useCart } from '@/modules/shop/composables/useCart';
import { GenericProduct } from '@/shared/interfaces/genericProduct';
import ItemAdder from '@/shared/components/ui/ItemAdder.vue';
import useQueue from '@/modules/queue/composables/useQueue';
import useToast from '@/shared/composables/useToast';
import { useCheckout } from '@/shared/composables/useCheckout';
import { TransactionType } from '@/enums/wallet.enum';
import { useCartStore } from '@/stores/shop/cartStore';
import { anykrowdApi } from '@/api/anykrowdApi';

// Init
const { t } = useI18n();
const route = useRoute();
const activityId = +route.params.activityId;
const selectedDate = route.query.date as string | undefined;
const selectedTimeSlot = route.query.timeSlot as string | undefined;
const isFromUnbooked = route.query.from_unbooked === 'true';
const bookingEventTicketIds = ref<number[]>([]);
const selectedSchedule = ref<any>(null);
const isLoadingSchedule = ref(false);

const { getQueueActivity, bookEventTicketSlot } = useQueue();
const { activity, isFetchedActivity } = getQueueActivity(activityId);
const { getCartTotalItemsByCategory, addToCart, decreaseProductQuantity, getCartItems, clearCart } = useCart();
const { presentToast } = useToast();
const { processCheckout } = useCheckout();
const router = useRouter();

// Load schedule data for availability checking
const loadScheduleAvailability = async () => {
	if (!selectedDate || !activityId) return;

	isLoadingSchedule.value = true;
	try {
		const schedulesResponse = await anykrowdApi.get(`/queue/activities/${activityId}/schedules`, true);
		const schedules = schedulesResponse.data.schedules || [];

		// Find the matching schedule for selected date/time
		if (selectedTimeSlot) {
			// For time slot queues, match by date and time slot
			const [startTime, endTime] = selectedTimeSlot.split('-');
			// Normalize time format (handle both "HH:MM" and "HH:MM:SS")
			const normalizeTime = (time: string) => time.substring(0, 5); // Take only HH:MM
			selectedSchedule.value = schedules.find((s: any) =>
				s.date === selectedDate &&
				normalizeTime(s.start_time || '') === normalizeTime(startTime) &&
				normalizeTime(s.end_time || '') === normalizeTime(endTime)
			);
		} else {
			// For chronological queues, match by date only
			selectedSchedule.value = schedules.find((s: any) => s.date === selectedDate);
		}
	} catch (error) {
		console.error('Error loading schedule availability:', error);
	} finally {
		isLoadingSchedule.value = false;
	}
};

// Get availability for a specific queue type (normal or fastlane)
const getAvailabilityForQueueType = (queueType: 'normal' | 'fastlane') => {
	if (!selectedSchedule.value?.availability) return null;

	const queueTypeKey = queueType === 'normal' ? 'normal' : 'fastlane';
	return selectedSchedule.value.availability[queueTypeKey] || null;
};

// Check if a ticket type is sold out
const isTicketTypeSoldOut = (queueType: 'normal' | 'fastlane') => {
	if (!selectedSchedule.value) return false; // Don't show as sold out if schedule not loaded yet
	const availability = getAvailabilityForQueueType(queueType);
	if (!availability) return false;
	return availability.available <= 0;
};

// Get available count for a queue type
const getAvailableCount = (queueType: 'normal' | 'fastlane') => {
	if (!selectedSchedule.value) return 999; // Return high number if schedule not loaded yet (to avoid false sold out)
	const availability = getAvailabilityForQueueType(queueType);
	return availability?.available ?? 0;
};

// Clear queue tickets from cart on component mount to avoid stale items
onMounted(async () => {
	const cartStore = useCartStore();
	// Remove only queue_ticket items from cart, keep other categories
	cartStore.cart = cartStore.cart.filter((item: any) => item.category !== 'queue_ticket');

	// Load event ticket IDs if booking from unbooked tickets
	if (isFromUnbooked) {
		const storedIds = sessionStorage.getItem('booking_event_ticket_ids');
		if (storedIds) {
			try {
				bookingEventTicketIds.value = JSON.parse(storedIds);
			} catch (e) {
				console.error('Error parsing booking event ticket IDs:', e);
			}
		}
	}

	// Load schedule availability if we have date/time selected
	if (selectedDate) {
		await loadScheduleAvailability();
	}
});

// Create ticket type objects that match the expected interface
const queueTicketTypes = computed(() => {
	if (!activity.value) return [];

	const types = [];

	// Regular pass
	if (activity.value.regular_price && activity.value.regular_currency_id) {
		const isSoldOut = isTicketTypeSoldOut('normal');
		const availableCount = getAvailableCount('normal');
		const soldOutLabel = isSoldOut ? ` (${t('queue.sold_out')})` : '';
		const availabilityLabel = !isSoldOut && availableCount > 0 && availableCount <= 5
			? ` (${availableCount} ${t('queue.available')})`
			: '';

		const regularProduct = new GenericProduct({
			id: activityId * 1000 + 1, // Generate unique ID for regular pass
			name: t('queue.regular_pass') + soldOutLabel,
			description: t('queue.regular_pass_description') + availabilityLabel,
			price: activity.value.regular_price / 100, // Convert from cents
			currency_id: activity.value.regular_currency_id,
			currency: activity.value.regularCurrency || {
				id: activity.value.regular_currency_id,
				prefix: '€',
				suffix: '',
				exchange_rate: 1
			},
			vat_percentage: 0,
			order: 1
		});
		(regularProduct as any).queue_type = 'regular';
		(regularProduct as any).isSoldOut = isSoldOut;
		types.push(regularProduct);
	}

	// Priority pass (fastlane)
	if (activity.value.priority_price && activity.value.priority_currency_id) {
		const isSoldOut = isTicketTypeSoldOut('fastlane');
		const availableCount = getAvailableCount('fastlane');
		const soldOutLabel = isSoldOut ? ` (${t('queue.sold_out')})` : '';
		const availabilityLabel = !isSoldOut && availableCount > 0 && availableCount <= 5
			? ` (${availableCount} ${t('queue.available')})`
			: '';

		const priorityProduct = new GenericProduct({
			id: activityId * 1000 + 2, // Generate unique ID for priority pass
			name: t('queue.priority_pass') + soldOutLabel,
			description: t('queue.priority_pass_description') + availabilityLabel,
			price: activity.value.priority_price / 100, // Convert from cents
			currency_id: activity.value.priority_currency_id,
			currency: activity.value.priorityCurrency || {
				id: activity.value.priority_currency_id,
				prefix: '€',
				suffix: '',
				exchange_rate: 1
			},
			vat_percentage: 0,
			order: 2
		});
		(priorityProduct as any).queue_type = 'fastlane';
		(priorityProduct as any).isSoldOut = isSoldOut;
		types.push(priorityProduct);
	}

	return types;
});

// Helper to get the count of a specific queue ticket type in cart
const getQueueTicketCount = (queueTicketType: any) => {
	const cartItems = getCartItems('queue_ticket');
	const matchingItems = cartItems.filter((cartItem: any) =>
		cartItem.product.id === queueTicketType.id
	);
	return matchingItems.reduce((sum: number, item: any) => sum + item.quantity, 0);
};

// Handle decrease product
const onDecreaseProductQuantity = (item: any) => {
	decreaseProductQuantity(item);
};

// Get the max bookings limit from activity
// Note: 0 means unlimited, so we use a high number (999) as the practical limit
const maxBookingsPerUser = computed(() => {
	if (!activity.value) return 10;
	const limit = activity.value.max_bookings_per_user;
	// If limit is 0, treat as unlimited (use 999 as practical limit)
	return limit === 0 ? 999 : limit;
});

// Handle increase product
const onIncreaseProductQuantity = async (item: any) => {
	const currentCount = getQueueTicketCount(item);
	const maxLimit = maxBookingsPerUser.value;

	// Check if ticket type is sold out
	const queueType = (item as any).queue_type;
	if (queueType === 'regular' || queueType === 'normal') {
		if (isTicketTypeSoldOut('normal')) {
			await presentToast('top', t('queue.error_sold_out'), 5000, 'danger');
			return;
		}
		const availableCount = getAvailableCount('normal');
		if (availableCount > 0 && currentCount >= availableCount) {
			await presentToast('top', `${t('queue.error_sold_out')} (${availableCount} ${t('queue.available') || 'available'})`, 5000, 'danger');
			return;
		}
	} else if (queueType === 'fastlane') {
		if (isTicketTypeSoldOut('fastlane')) {
			await presentToast('top', t('queue.error_sold_out'), 5000, 'danger');
			return;
		}
		const availableCount = getAvailableCount('fastlane');
		if (availableCount > 0 && currentCount >= availableCount) {
			await presentToast('top', `${t('queue.error_sold_out')} (${availableCount} ${t('queue.available') || 'available'})`, 5000, 'danger');
			return;
		}
	}

	if (currentCount >= maxLimit) {
		const message = maxLimit === 1
			? t('queue.ticket_limit_reached')
			: `${t('queue.ticket_limit_reached')} (Maximum: ${maxLimit})`;
		await presentToast('top', message, 5000, 'danger');
		return;
	}

	addToCart(item, 'queue_ticket', {
		activityId: activityId,
		queueType: (item as any).queue_type || (item as any).queueType
	});
};

const canAddMore = (item: any) => {
	const currentCount = getQueueTicketCount(item);
	const maxLimit = maxBookingsPerUser.value;

	// Check if ticket type is sold out
	const queueType = (item as any).queue_type;
	if (queueType === 'regular' || queueType === 'normal') {
		if (isTicketTypeSoldOut('normal')) {
			return false;
		}
		// Also check availability limit
		const availableCount = getAvailableCount('normal');
		if (availableCount > 0 && currentCount >= availableCount) {
			return false;
		}
	} else if (queueType === 'fastlane') {
		if (isTicketTypeSoldOut('fastlane')) {
			return false;
		}
		// Also check availability limit
		const availableCount = getAvailableCount('fastlane');
		if (availableCount > 0 && currentCount >= availableCount) {
			return false;
		}
	}

	return currentCount < maxLimit;
};

// Handle checkout
const handleCheckout = async () => {
	// If booking from unbooked event tickets, use the booking endpoint directly
	if (isFromUnbooked && bookingEventTicketIds.value.length > 0 && selectedDate) {
		// Book each event ticket
		let successCount = 0;
		let errorCount = 0;

		for (const eventTicketId of bookingEventTicketIds.value) {
			try {
				await bookEventTicketSlot(eventTicketId, {
					target_date: selectedDate,
					target_time_slot: selectedTimeSlot,
				});
				successCount++;
			} catch (error) {
				console.error(`Failed to book event ticket ${eventTicketId}:`, error);
				errorCount++;
			}
		}

		// Clear stored IDs
		sessionStorage.removeItem('booking_event_ticket_ids');
		sessionStorage.removeItem('unbooked_event_ticket_ids');

		// Show result message
		if (successCount > 0 && errorCount === 0) {
			await presentToast('top', t('queue.all_tickets_booked_successfully'), 3000, 'success');
			router.push('/my-queues');
		} else if (successCount > 0 && errorCount > 0) {
			await presentToast('top', t('queue.some_tickets_booked_successfully', { success: successCount, total: bookingEventTicketIds.value.length }), 5000, 'warning');
			router.push('/my-queues');
		} else {
			await presentToast('top', t('queue.booking_failed'), 5000, 'danger');
		}

		return;
	}

	// Regular checkout flow for new bookings
	await processCheckout('QUEUE_BOOKING', {
		activityId,
		selectedDate,
		selectedTimeSlot
	});
};
</script>

<template>
	<ion-content>
		<div class="flex flex-col justify-between px-[34px]">
			<div class="flex flex-col mt-12 mb-10 items-center">
				<div class="flex items-center justify-center mb-4">
					<ion-button
						class="rounded ion-no-padding ion-no-margin w-[60px] h-[60px] hover:rounded-full hover:bg-white/20"
						:routerLink="`/queue/${activityId}`">
						<ion-icon class="text-[38px] text-white cursor-pointer" :icon="closeOutline"></ion-icon>
					</ion-button>
				</div>
				<h1 class="font-sourcesanspro-semibold text-lg2">{{ activity?.name }}</h1>
			</div>

			<div v-if="!isFetchedActivity" class="flex items-center justify-center mb-4">
				<ion-spinner name="dots"></ion-spinner>
			</div>

		<div v-if="activity && isFetchedActivity && queueTicketTypes.length">
			<div class="mb-8">
				<!-- Show message for unbooked tickets -->
				<div v-if="isFromUnbooked && bookingEventTicketIds.length > 0" class="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
					<p class="text-white/80 text-sm">
						{{ t('queue.booking_unbooked_tickets', { count: bookingEventTicketIds.length }) }}
					</p>
				</div>

				<div class="flex items-center mb-4">
					<ion-icon class="mr-2 text-[20px] text-white cursor-pointer" :icon="ticketOutline"></ion-icon>
					<h2 class="font-sourcesanspro-semibold text-lg inline">{{ t('queue.choose_your_pass') }}</h2>
				</div>
					<div>
						<ItemAdder
							v-for="(queueTicketType, idx) in queueTicketTypes"
							:key="idx"
							@onDecreaseProductQuantity="onDecreaseProductQuantity"
							@onIncreaseProductQuantity="onIncreaseProductQuantity"
							:id="queueTicketType.id"
							:description="queueTicketType.description"
							:expandable="queueTicketType.description ? true : false"
							:productId="queueTicketType.id"
							:product="queueTicketType"
							class="mb-4"
							:title="queueTicketType.name"
							:price="queueTicketType.price"
							:currency="queueTicketType.currency"
							:quantity="getQueueTicketCount(queueTicketType)"
							:can-add-more="canAddMore(queueTicketType)"
						/>
					</div>
				</div>
			</div>

			<div v-else-if="activity && isFetchedActivity && queueTicketTypes.length === 0" class="mb-8 text-center">
				<p class="text-white/70">{{ t('queue.no_ticket_types_available') }}</p>
			</div>

		<div class="mb-8">
			<ion-button
				v-if="isFetchedActivity"
				:disabled="isFromUnbooked ? false : getCartTotalItemsByCategory('queue_ticket') === 0"
				class="w-full hover:rounded-full hover:bg-white"
				expand="full"
				size="large"
				shape="round"
				@click="handleCheckout()">
				<div class="flex w-full items-center justify-start">
					<span class="text-m2">{{ isFromUnbooked ? t('queue.book_now') : t('queue.continue_to_checkout') }}</span>
				</div>
			</ion-button>
		</div>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped>
ion-searchbar {
	--box-shadow: none;
}
</style>